import { useState, type ChangeEvent, type FormEvent } from "react";
import { ZodError, type ZodIssue } from "zod";
import {
  parseReportRequest,
  type ReportRequest,
} from "../../domain/report/contracts";
import {
  facingDirectionLabels,
  facingDirections,
} from "../../domain/report/compass";
import { lookupVietnamProvince } from "../../domain/report/vietnam-geocoding";

type FormStatus = "idle" | "invalid" | "valid";

type IntakeFormProps = {
  onValidSubmit?: (request: ReportRequest) => void | Promise<void>;
};

function text(formData: FormData, key: string): string {
  return String(formData.get(key) ?? "");
}

function optionalNumber(formData: FormData, key: string): number | undefined {
  const raw = text(formData, key).trim();
  if (!raw) {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : Number.NaN;
}

const fieldNames: Record<string, string> = {
  fullName: "Họ và tên",
  calculationGender: "Giới tính",
  calendar: "Hệ lịch",
  birthDate: "Ngày sinh",
  birthTime: "Giờ sinh",
  birthTimeConfidence: "Độ chắc chắn giờ sinh",
  countryCode: "Quốc gia",
  birthLocality: "Nơi sinh",
  locality: "Nơi sinh",
  ianaTimeZone: "Múi giờ",
  latitude: "Vĩ độ",
  longitude: "Kinh độ",
  phoneNumber: "Số điện thoại",
  currentProvince: "Tỉnh/thành hiện tại",
  address: "Địa chỉ không gian",
  homeAddress: "Địa chỉ không gian",
  spaceType: "Loại không gian",
  facingDirection: "Hướng đang ở",
  directionConfidence: "Độ chắc chắn hướng",
  question: "Vấn đề muốn hỏi sâu",
  adultConfirmed: "Xác nhận 18 tuổi",
  processingConsent: "Đồng ý xử lý dữ liệu",
  aiConsent: "Đồng ý dùng AI",
};

const messageOverrides: Record<string, string> = {
  birthTime: "phải theo định dạng HH:mm",
  phoneNumber: "cần từ 9 đến 15 chữ số",
  birthDate: "phải là ngày hợp lệ (YYYY-MM-DD)",
  adultConfirmed: "cần đánh dấu xác nhận",
  processingConsent: "cần đánh dấu đồng ý",
  aiConsent: "cần đánh dấu đồng ý",
  question: "cần viết rõ hơn (tối thiểu 10 ký tự)",
  homeAddress: "cần địa chỉ đầy đủ hơn (tối thiểu 5 ký tự)",
};

function vietnameseMessage(issue: ZodIssue): string {
  const key = String(issue.path[issue.path.length - 1] ?? "");
  const override = messageOverrides[key];
  if (override) {
    return override;
  }

  const data = issue as unknown as Record<string, unknown>;
  switch (issue.code) {
    case "too_small":
      return `cần tối thiểu ${String(data.minimum ?? "")} ký tự`;
    case "too_big":
      return `chỉ tối đa ${String(data.maximum ?? "")}`;
    case "invalid_value":
      return `hãy chọn một giá trị hợp lệ`;
    case "invalid_format":
      return `định dạng chưa đúng (${String(data.format ?? "")})`;
    case "invalid_type":
      return "thiếu hoặc sai kiểu dữ liệu";
    case "unrecognized_keys":
      return "có trường không được phép";
    default:
      return issue.message;
  }
}

function describeIssue(issue: ZodIssue): string {
  const key = String(issue.path[issue.path.length - 1] ?? "");
  const field = fieldNames[key] ?? issue.path.join(".") ?? "Hồ sơ";
  return `• ${field}: ${vietnameseMessage(issue)}`;
}

export function IntakeForm({ onValidSubmit }: IntakeFormProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [issues, setIssues] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [geoNote, setGeoNote] = useState("");

  function handleLocalityChange(event: ChangeEvent<HTMLInputElement>) {
    const hit = lookupVietnamProvince(event.target.value);
    if (!hit) {
      setGeoNote("");
      return;
    }
    const form = event.currentTarget.form;
    if (form) {
      (form.elements.namedItem("latitude") as HTMLInputElement).value = String(
        hit.latitude,
      );
      (form.elements.namedItem("longitude") as HTMLInputElement).value = String(
        hit.longitude,
      );
      (form.elements.namedItem("ianaTimeZone") as HTMLInputElement).value =
        hit.ianaTimeZone;
      (form.elements.namedItem("countryCode") as HTMLInputElement).value =
        hit.countryCode;
    }
    setGeoNote(
      `Đã tự điền tọa độ ${hit.name} (${hit.latitude}, ${hit.longitude}).`,
    );
  }

  async function validateLocally(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const request = parseReportRequest({
        fullName: text(formData, "fullName"),
        calculationGender: text(formData, "calculationGender"),
        calendar: text(formData, "calendar"),
        birthDate: text(formData, "birthDate"),
        birthTime: text(formData, "birthTime"),
        birthTimeConfidence: text(formData, "birthTimeConfidence"),
        birthPlace: {
          countryCode: text(formData, "countryCode"),
          locality: text(formData, "birthLocality"),
          latitude: optionalNumber(formData, "latitude"),
          longitude: optionalNumber(formData, "longitude"),
          ianaTimeZone: text(formData, "ianaTimeZone"),
        },
        phoneNumber: text(formData, "phoneNumber"),
        currentProvince: text(formData, "currentProvince"),
        homeContext: {
          address: text(formData, "homeAddress"),
          spaceType: text(formData, "homeSpaceType"),
          facingDirection: text(formData, "facingDirection") || undefined,
          directionConfidence: text(formData, "homeDirectionConfidence"),
        },
        question: text(formData, "question"),
        adultConfirmed: formData.has("adultConfirmed"),
        processingConsent: formData.has("processingConsent"),
        aiConsent: formData.has("aiConsent"),
      });
      setStatus("valid");
      if (onValidSubmit) {
        setBusy(true);
        try {
          await onValidSubmit(request);
        } finally {
          setBusy(false);
        }
      }
    } catch (error) {
      setStatus("invalid");
      setIssues(
        error instanceof ZodError
          ? error.issues.map(describeIssue)
          : ["Dữ liệu chưa hợp lệ, vui lòng thử lại."],
      );
    }
  }

  return (
    <section
      className="intake-card lacquer-card"
      aria-labelledby="intake-title"
    >
      <div className="section-heading">
        <p className="eyebrow">Thiết Lập Đương Số</p>
        <h2 id="intake-title">Thiết Lập Đương Số</h2>
        <p>
          Biểu mẫu hiện chỉ kiểm tra trên thiết bị. Dữ liệu chưa được gửi tới
          Supabase hay AI.
        </p>
      </div>

      <form className="intake-form" onSubmit={validateLocally} noValidate>
        <fieldset>
          <legend>Định Danh</legend>
          <div className="field-grid">
            <label>
              Họ và tên
              <input name="fullName" autoComplete="name" required />
            </label>
            <label>
              Giới tính tính toán
              <select name="calculationGender" defaultValue="" required>
                <option value="" disabled>
                  Chọn giới tính
                </option>
                <option value="nam">Nam</option>
                <option value="nu">Nữ</option>
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Ma Trận Sinh Thần</legend>
          <div className="field-grid">
            <label>
              Hệ lịch
              <select name="calendar" defaultValue="solar">
                <option value="solar">Dương lịch</option>
                <option value="lunar">Âm lịch</option>
              </select>
            </label>
            <label>
              Ngày sinh
              <input name="birthDate" type="date" required />
            </label>
            <label>
              Giờ sinh
              <input name="birthTime" type="time" required />
            </label>
            <label>
              Độ chắc chắn giờ sinh
              <select name="birthTimeConfidence" defaultValue="exact">
                <option value="exact">Chính xác</option>
                <option value="approximate">Gần đúng</option>
                <option value="unknown">Không chắc</option>
              </select>
            </label>
            <label>
              Nơi sinh
              <input
                name="birthLocality"
                autoComplete="off"
                required
                onChange={handleLocalityChange}
              />
              {geoNote ? (
                <span className="jade-badge">{geoNote}</span>
              ) : (
                <span className="field-help">
                  Nhập tên tỉnh/thành (VD: Hà Nội, TP. Hồ Chí Minh, Nha
                  Trang...) — hệ thống tự điền tọa độ.
                </span>
              )}
            </label>
            <label>
              Quốc gia
              <input
                name="countryCode"
                defaultValue="VN"
                maxLength={2}
                required
              />
            </label>
            <label>
              Vĩ độ
              <input name="latitude" type="number" step="any" />
            </label>
            <label>
              Kinh độ
              <input name="longitude" type="number" step="any" />
            </label>
            <label>
              Múi giờ IANA
              <input
                name="ianaTimeZone"
                defaultValue="Asia/Ho_Chi_Minh"
                required
              />
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend>Hiện Trạng Không Gian</legend>
          <p className="field-help">
            Dữ liệu chỉ được kiểm tra trên thiết bị, chưa được tải lên hoặc dùng
            để tạo khuyến nghị Phong Thủy.
          </p>
          <div className="field-grid">
            <label>
              Số điện thoại
              <input
                name="phoneNumber"
                type="tel"
                autoComplete="tel"
                required
              />
            </label>
            <label>
              Tỉnh/thành hiện tại
              <input
                name="currentProvince"
                autoComplete="address-level1"
                required
              />
            </label>
            <label>
              Địa chỉ không gian
              <input
                name="homeAddress"
                autoComplete="street-address"
                required
              />
            </label>
            <label>
              Loại không gian
              <select name="homeSpaceType" defaultValue="" required>
                <option value="" disabled>
                  Chọn loại không gian
                </option>
                <option value="house">Nhà</option>
                <option value="apartment">Căn hộ</option>
                <option value="room">Phòng</option>
                <option value="office">Văn phòng</option>
                <option value="shop">Cửa hàng</option>
              </select>
            </label>
            <label>
              Hướng đang ở
              <select name="facingDirection" defaultValue="">
                <option value="">Không rõ / bỏ qua</option>
                {facingDirections.map((id) => (
                  <option key={id} value={id}>
                    {facingDirectionLabels[id]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Độ chắc chắn hướng
              <select name="homeDirectionConfidence" defaultValue="exact">
                <option value="exact">Chính xác</option>
                <option value="approximate">Gần đúng</option>
                <option value="unknown">Không chắc</option>
              </select>
            </label>
          </div>
        </fieldset>

        <label className="question-field">
          Vấn đề muốn hỏi sâu
          <textarea name="question" rows={5} required />
        </label>

        <fieldset className="consent-list">
          <legend>Xác nhận bắt buộc</legend>
          <label>
            <input name="adultConfirmed" type="checkbox" required />
            Tôi xác nhận đã đủ 18 tuổi.
          </label>
          <label>
            <input name="processingConsent" type="checkbox" required />
            Tôi đồng ý xử lý dữ liệu để tạo báo cáo.
          </label>
          <label>
            <input name="aiConsent" type="checkbox" required />
            Tôi đồng ý dùng AI để diễn đạt facts và căn cứ đã kiểm soát.
          </label>
        </fieldset>

        {status === "invalid" ? (
          <div className="form-message error" role="alert">
            Vui lòng kiểm tra lại dữ liệu và ba xác nhận bắt buộc.
            {issues.length > 0 ? (
              <ul className="issue-list">
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {status === "valid" ? (
          <div className="form-message success" role="status">
            Dữ liệu hợp lệ và chưa được tải lên hoặc lưu trữ.
          </div>
        ) : null}

        <button type="submit" className="btn-gold" disabled={busy}>
          Khởi Tạo Bản Luận Giải Hoàng Kim
        </button>
      </form>
    </section>
  );
}
